import { useEffect, useRef } from "react";
import { useQuery } from "react-query";
import { apiV2 } from "../../../api/axiosConfig";
import { useMapSettings } from "../../operational-map/context/useMapSettings";

// --- Types ---

type PointLocation = {
    type: "Point";
    coordinates: [number, number]; // [lng, lat]
    properties?: { radius?: number };
};

type PolygonLocation = {
    type: "Polygon";
    coordinates: [number, number][][];
};

export type MapZone = {
    id: string;
    name: string;
    clientID: string;
    clientName?: string;
    departmentName?: string;
    alwaysActive: boolean;
    startsAt?: string;
    expiresAt?: string;
    userNames: string[];
    departmentNames: string[];
    location: PointLocation | PolygonLocation;
};

export type ZoneKind = "geofence" | "safety-zone";

export type ZoneClickData = {
    zone: MapZone;
    kind: ZoneKind;
};

// --- Colours ---

const GEOFENCE_STROKE        = "#ff7300";
const GEOFENCE_FILL          = "#ff7300";
const GEOFENCE_STROKE_HOVER  = "#cc5a00";
const GEOFENCE_FILL_HOVER    = "#ff7300";
const SAFETY_STROKE          = "#00b36b";
const SAFETY_FILL            = "#00b36b";
const SAFETY_STROKE_HOVER    = "#007a4a";
const SAFETY_FILL_HOVER      = "#00b36b";
const FILL_OPACITY           = 0.18;
const FILL_OPACITY_HOVER     = 0.38;
const STROKE_OPACITY         = 0.8;
const STROKE_WEIGHT          = 2;
const STROKE_WEIGHT_HOVER    = 3;

// --- Fetch ---

async function fetchGeofences(clientID: string | undefined): Promise<MapZone[]> {
    const res = await apiV2.get("ops/geofences", { params: clientID ? { clientID } : {} });
    return res.data as MapZone[];
}

async function fetchSafetyZones(clientID: string | undefined): Promise<MapZone[]> {
    const res = await apiV2.get("ops/safety-zones", { params: clientID ? { clientID } : {} });
    return res.data as MapZone[];
}

// --- Shape management ---

type ShapeEntry = {
    zone: MapZone;
    shape: google.maps.Circle | google.maps.Polygon;
    kind: "circle" | "polygon";
};

type ShapeRef = {
    entries: ShapeEntry[];
};

function clearShapes(shapes: ShapeRef) {
    shapes.entries.forEach((e) => e.shape.setMap(null));
    shapes.entries = [];
}

function applyNormal(
    entry: ShapeEntry,
    strokeColor: string,
    fillColor: string,
) {
    entry.shape.setOptions({
        strokeColor,
        strokeOpacity: STROKE_OPACITY,
        strokeWeight: STROKE_WEIGHT,
        fillColor,
        fillOpacity: FILL_OPACITY,
        zIndex: 1,
    });
}

function applyHover(
    entry: ShapeEntry,
    strokeColor: string,
    fillColor: string,
) {
    entry.shape.setOptions({
        strokeColor,
        strokeOpacity: STROKE_OPACITY,
        strokeWeight: STROKE_WEIGHT_HOVER,
        fillColor,
        fillOpacity: FILL_OPACITY_HOVER,
        zIndex: 10,
    });
}

function renderZones(
    map: google.maps.Map,
    zones: MapZone[],
    zoneKind: ZoneKind,
    shapes: ShapeRef,
    onClickZone: (data: ZoneClickData) => void,
    onForceCursor: (cursor: string) => void,
) {
    clearShapes(shapes);

    const strokeColor = zoneKind === "geofence" ? GEOFENCE_STROKE : SAFETY_STROKE;
    const fillColor   = zoneKind === "geofence" ? GEOFENCE_FILL   : SAFETY_FILL;
    const strokeHover = zoneKind === "geofence" ? GEOFENCE_STROKE_HOVER : SAFETY_STROKE_HOVER;
    const fillHover   = zoneKind === "geofence" ? GEOFENCE_FILL_HOVER   : SAFETY_FILL_HOVER;

    for (const zone of zones) {
        const loc = zone.location;
        let shape: google.maps.Circle | google.maps.Polygon;
        let kind: "circle" | "polygon";

        if (loc.type === "Point") {
            const radius = loc.properties?.radius;
            if (!radius) continue;
            const [lng, lat] = loc.coordinates;
            shape = new google.maps.Circle({
                map,
                center: { lat, lng },
                radius,
                strokeColor,
                strokeOpacity: STROKE_OPACITY,
                strokeWeight: STROKE_WEIGHT,
                fillColor,
                fillOpacity: FILL_OPACITY,
                zIndex: 1,
            });
            kind = "circle";
        } else if (loc.type === "Polygon") {
            const path = loc.coordinates.flat().map(([lng, lat]) => ({ lat, lng }));
            shape = new google.maps.Polygon({
                map,
                paths: path,
                strokeColor,
                strokeOpacity: STROKE_OPACITY,
                strokeWeight: STROKE_WEIGHT,
                fillColor,
                fillOpacity: FILL_OPACITY,
                zIndex: 1,
            });
            kind = "polygon";
        } else {
            continue;
        }

        const entry: ShapeEntry = { zone, shape, kind };
        shapes.entries.push(entry);

        shape.addListener("mouseover", () => { applyHover(entry, strokeHover, fillHover); onForceCursor("pointer"); });
        shape.addListener("mouseout",  () => { applyNormal(entry, strokeColor, fillColor); onForceCursor("grab"); });
        shape.addListener("click",     () => onClickZone({ zone, kind: zoneKind }));
    }
}

// --- Hook ---

export function useZoneShapesLayer(
    map: google.maps.Map | null,
    onClickZone: (data: ZoneClickData) => void,
    forceCursor?: (cursor: string) => void,
) {
    const { settings } = useMapSettings();
    const { clientID, siteTypes } = settings;

    const geofenceShapes   = useRef<ShapeRef>({ entries: [] });
    const safetyZoneShapes = useRef<ShapeRef>({ entries: [] });
    const onClickRef       = useRef(onClickZone);
    onClickRef.current     = onClickZone;
    const forceCursorRef   = useRef(forceCursor);
    forceCursorRef.current = forceCursor;

    const showGeofences   = siteTypes.includes("geofence");
    const showSafetyZones = siteTypes.includes("safety-zone");

    const { data: geofences } = useQuery(
        ["ops-geofences", clientID, showGeofences],
        () => fetchGeofences(clientID),
        { refetchInterval: 60_000, enabled: !!map && showGeofences },
    );

    const { data: safetyZones } = useQuery(
        ["ops-safety-zones", clientID, showSafetyZones],
        () => fetchSafetyZones(clientID),
        { refetchInterval: 60_000, enabled: !!map && showSafetyZones },
    );

    useEffect(() => {
        if (!map) return;
        if (!showGeofences) { clearShapes(geofenceShapes.current); return; }
        renderZones(map, geofences ?? [], "geofence", geofenceShapes.current, (d) => onClickRef.current(d), (c) => forceCursorRef.current?.(c));
    }, [map, geofences, showGeofences]);

    useEffect(() => {
        if (!map) return;
        if (!showSafetyZones) { clearShapes(safetyZoneShapes.current); return; }
        renderZones(map, safetyZones ?? [], "safety-zone", safetyZoneShapes.current, (d) => onClickRef.current(d), (c) => forceCursorRef.current?.(c));
    }, [map, safetyZones, showSafetyZones]);

    useEffect(() => {
        return () => {
            clearShapes(geofenceShapes.current);
            clearShapes(safetyZoneShapes.current);
        };
    }, [map]);

    return {
        geofenceCount:   showGeofences   ? (geofences?.length   ?? 0) : 0,
        safetyZoneCount: showSafetyZones ? (safetyZones?.length ?? 0) : 0,
    };
}
